import type { Level, Product, Service } from "@/modules/product/types";

export type ServiceParameters = {
    level: string;
    levelId?: number | null;
    segment?: string;
    segmentId?: number | null;
    startDate: string;
    endDate: string;
    baseProductEnum?: string;
    metricIds?: number[];
};

export type ProductInfo = Pick<Product, "id" | "name"> & { services?: Pick<Service, "id" | "name" | "url">[] };

export type ServiceEntry = {
    productId: number;
    productName: string;
    serviceId: number;
    serviceSlug: string;
    serviceDisplayName: string;
};

export interface ServiceParamsInput {
    levelName: string;
    levelId?: number | null;
    segmentName?: string;
    segmentId?: number | null;
    metricIds?: number[];
    startDate: string;
    endDate: string;
}

export function buildServiceParameters(input: ServiceParamsInput): ServiceParameters {
    const params: ServiceParameters = {
        level: input.levelName,
        startDate: input.startDate,
        endDate: input.endDate,
    };

    if (input.levelId != null) {
        params.levelId = input.levelId;
    }

    if (input.segmentName) {
        params.segment = input.segmentName;
    }

    if (input.segmentId != null) {
        params.segmentId = input.segmentId;
    }

    if (input.metricIds && input.metricIds.length > 0) {
        params.metricIds = input.metricIds;
    }

    return params;
}

export const buildServiceUrl = (slug?: string) => {
    if (!slug) return "/paidsearch";
    return slug.startsWith("/") ? slug : `/${slug}`;
};

interface ResolvePrimaryServiceInput {
    serviceEntries: ServiceEntry[];
    levelsByServiceId: Record<number, Level[]>;
    levelNameLower: string;
    selectedLevelServiceId?: number;
    firstMetricServiceId?: number;
    firstProductId?: number;
}

export function resolvePrimaryService(input: ResolvePrimaryServiceInput): ServiceEntry | undefined {
    const { serviceEntries, levelsByServiceId, levelNameLower, selectedLevelServiceId, firstMetricServiceId, firstProductId } = input;

    const serviceSupportsLevel = (entry?: { serviceId: number }) => {
        if (!entry || !levelNameLower) return false;
        const serviceLevels = levelsByServiceId[entry.serviceId] || [];
        return serviceLevels.some((lvl) => lvl.name.toLowerCase() === levelNameLower);
    };

    const findServiceById = (serviceId?: number) => serviceEntries.find((entry) => serviceId !== undefined && entry.serviceId === serviceId);

    const findServicesForProduct = (productId?: number) => {
        if (productId === undefined) return [];
        return serviceEntries.filter((entry) => entry.productId === productId && serviceSupportsLevel(entry));
    };

    let entry = findServiceById(selectedLevelServiceId);

    if (!entry) {
        entry = findServiceById(firstMetricServiceId);
    }

    if (!entry && firstProductId !== undefined) {
        entry = findServicesForProduct(firstProductId)[0];
    }

    if (!entry) {
        entry = serviceEntries.find((e) => serviceSupportsLevel(e)) || serviceEntries[0];
    }

    return entry;
}

interface BuildDataSourcesInput {
    productIds: number[];
    productById: Record<number, ProductInfo>;
    serviceEntries: ServiceEntry[];
    levelsByServiceId: Record<number, Level[]>;
    levelNameLower: string;
    primaryServiceSlug?: string;
    serviceParams: ServiceParameters;
}

export function buildDataSources(input: BuildDataSourcesInput) {
    const { productIds, productById, serviceEntries, levelsByServiceId, levelNameLower, primaryServiceSlug, serviceParams } = input;

    const serviceSupportsLevel = (entry?: { serviceId: number }) => {
        if (!entry || !levelNameLower) return false;
        const serviceLevels = levelsByServiceId[entry.serviceId] || [];
        return serviceLevels.some((lvl) => lvl.name.toLowerCase() === levelNameLower);
    };

    const findServicesForProduct = (productId: number) => serviceEntries.filter((entry) => entry.productId === productId && serviceSupportsLevel(entry));

    return productIds.flatMap((productId) => {
        const product = productById[productId];
        if (!product) return [];

        const servicesForProduct = findServicesForProduct(productId);
        if (servicesForProduct.length === 0) {
            return [
                {
                    url: buildServiceUrl(primaryServiceSlug),
                    productId: product.id,
                    productName: product.name,
                    parameters: { ...serviceParams },
                    sourceLabel: product.name,
                },
            ];
        }

        return servicesForProduct.map((serviceEntry) => ({
            url: buildServiceUrl(serviceEntry.serviceSlug),
            productId: product.id,
            productName: product.name,
            parameters: { ...serviceParams },
            sourceLabel: `${product.name} - ${serviceEntry.serviceDisplayName}`,
        }));
    });
}
